// syncService.js - Servicio de sincronización con SQL Server
import { db } from '../libraries/db';

class SyncService {
    constructor(apiBaseUrl) {
        // Si no se pasa URL, usar la variable de entorno
        // this.apiBaseUrl = `https://gestion.jnegre.com:44343`;
        this.apiBaseUrl = `https://localhost:44343`;
        this.syncInProgress = false;
        this.lastSyncDate = this.getLastSyncDate();
    }
    // ============================================================================
    // CONFIGURACIÓN Y ESTADO
    // ============================================================================

    configure(apiBaseUrl) {
        this.apiBaseUrl = apiBaseUrl;
        console.log(`✅ SyncService configurado con URL: ${apiBaseUrl}`);
    }

    getLastSyncDate() {
        const stored = localStorage.getItem('lastSyncDate');
        return stored ? new Date(stored) : null;
    }

    setLastSyncDate(date) {
        this.lastSyncDate = date;
        localStorage.setItem('lastSyncDate', date.toISOString());
    }

    isSyncing() {
        return this.syncInProgress;
    }

    // ============================================================================
    // SINCRONIZACIÓN INICIAL - Descargar todos los datos del servidor
    // ============================================================================

    async initialSync(onProgress = null) {
        if (this.syncInProgress) {
            console.warn('Sincronización ya en progreso');
            return { success: false, error: 'Sync in progress' };
        }

        this.syncInProgress = true;
        console.log('🔄 Iniciando sincronización inicial...');

        try {
            const tablas = [
                'SYS_Usuarios',
                'SYS_Perfiles',
                //'SYS_Correos',
                'GES_Clientes',
                //'GES_ClientesContactos',
                //'GES_Productos',
                //'GES_Proveedores',
                'GES_Analisis',
                //'GES_Elementos',
                'GES_ElementosPlanta',
                'GES_Tareas',
                'GES_ComentariosElementos',
                //'GES_ComentariosPrivados',
                'GES_ObservacionesElementos',
                'GES_OfertasClientes',
                //'GES_OfertasContactos',
                //'GES_OfertasProductos',
                'GES_ParametrosAnalisisPlanta',
                'GES_ParametrosElementoPlantaCliente',
                'GES_ValorParametros',
                'GES_Conf_Analisis_NivelesPlantasCliente',
                'GES_Conf_NivelesPlantasCliente',
                'GES_Conf_PlantasCliente',
                //'GES_Consumos',
                //'GES_Entregas',
                //'GES_HistorialCorreosEnviados',
                //'GES_ModoEnvio',
                'LST_Parametros',
                //'LST_Unidades',
                //'LST_Provincias',
                //'LST_Poblaciones',
                //'LST_Comarcas'
            ];

            let processedTables = 0;
            const totalTables = tablas.length;

            for (const tabla of tablas) {
                try {
                    console.log(`📥 Descargando ${tabla}...`);

                    // Llamar a tu API para obtener los datos - INCLUIR TODOS (incluso deleted=1)
                    const response = await fetch(`${this.apiBaseUrl}/api/sync/${tabla}?includeDeleted=true`, {
                        method: 'GET',
                        headers: {
                            'Content-Type': 'application/json',
                            // Agregar autenticación si es necesario
                            // 'Authorization': `Bearer ${token}`
                        }
                    });

                    if (!response.ok) {
                        throw new Error(`Error descargando ${tabla}: ${response.statusText}`);
                    }

                    const datos = await response.json();

                    // Guardar en IndexedDB
                    if (datos && Array.isArray(datos) && datos.length > 0) {
                        await db.table(tabla).bulkPut(datos);
                        console.log(`✅ ${tabla}: ${datos.length} registros guardados`);
                    } else {
                        console.log(`⚠️ ${tabla}: Sin datos`);
                    }

                    processedTables++;

                    // Reportar progreso
                    if (onProgress) {
                        onProgress({
                            tabla,
                            processed: processedTables,
                            total: totalTables,
                            percentage: Math.round((processedTables / totalTables) * 100)
                        });
                    }

                } catch (error) {
                    console.error(`❌ Error sincronizando ${tabla}:`, error);
                    // Continuar con las demás tablas
                }
            }

            // Marcar fecha de sincronización
            this.setLastSyncDate(new Date());

            console.log('✅ Sincronización inicial completada');
            return {
                success: true,
                tablesProcessed: processedTables,
                totalTables
            };

        } catch (error) {
            console.error('❌ Error en sincronización inicial:', error);
            return { success: false, error: error.message };
        } finally {
            this.syncInProgress = false;
        }
    }

    // ============================================================================
    // LIMPIAR BASE DE DATOS LOCAL (IndexedDB)
    // ============================================================================
    async limpiarBaseDatosLocal() {
        try {
            console.log('🧹 Limpiando base de datos local (IndexedDB)...');
            // Borrar todas las tablas definidas en Dexie
            if (db && Array.isArray(db.tables)) {
                const clearPromises = db.tables
                .filter(t => t.name !== 'PendingSync')
                .map(t => t.clear());
                await Promise.all(clearPromises);
            } else if (db) {
                // Fallback: intentar delete() si está disponible
                if (typeof db.delete === 'function') {
                    await db.delete();
                    // recreate db instance by reopening
                    await db.open();
                }
            }

            // Limpiar fecha de última sincronización
            localStorage.removeItem('lastSyncDate');
            this.lastSyncDate = null;

            console.log('✅ Base de datos local limpiada');
            return { success: true };
        } catch (error) {
            console.error('❌ Error limpiando la base de datos local:', error);
            return { success: false, error: error.message };
        }
    }

// ============================================================================
// SINCRONIZACIÓN INCREMENTAL - Solo cambios desde la última sync
// ============================================================================

    async incrementalSync() {
        if (this.syncInProgress) {
            console.warn('Sincronización ya en progreso');
            return { success: false, error: 'Sync in progress' };
        }

        this.syncInProgress = true;
        console.log('🔄 Iniciando sincronización incremental...');

        try {
            const since = this.lastSyncDate ?? new Date(0);

            const response = await fetch(`${this.apiBaseUrl}/api/sync/pull`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ since: since.toISOString(), tables: 'all' })
            });

            if (!response.ok) {
                throw new Error(`Error en pull: ${response.statusText}`);
            }

            const cambios = await response.json();

            // Guardar cada tabla que haya traído cambios
            for (const [tabla, datos] of Object.entries(cambios)) {
                if (Array.isArray(datos) && datos.length > 0) {
                    await db.table(tabla).bulkPut(datos);
                    console.log(`✅ ${tabla}: ${datos.length} registros actualizados`);
                }
            }

            this.setLastSyncDate(new Date());
            console.log('✅ Sincronización incremental completada');
            return { success: true };

        } catch (error) {
            console.error('❌ Error en sincronización incremental:', error);
            return { success: false, error: error.message };
        } finally {
            this.syncInProgress = false;
        }
    }
    }

export const syncService = new SyncService();